/**
 * PlaceIndex — the PoI scan's source of PLACES (settlements), read from the WORLD
 * rather than from what the renderer happens to have drawn.
 *
 * WHY NOT JUST ASK THE RENDERER
 * -----------------------------
 * The obvious source is `SettlementRenderer.loaded()` — the settlements it has
 * built. But that set is the RENDER set: it holds exactly the cells inside the
 * camera's resident 3×3 region ring, plus whatever earlier cells have not yet
 * aged out past DISPOSE_DIST. Two consequences, both of them bugs the player felt:
 *
 *   - A player who SPAWNS on a planet and never leaves has streamed nothing but
 *     the ring under their feet, so the scan showed nothing — the town two
 *     kilometres away does not exist until the renderer decides to draw it. Fly
 *     off-world and come back and the accumulated ring made it "work".
 *   - What HAD accumulated was unbounded: settlements built an hour ago, half a
 *     world away, still tagged — the overlay drifted away from "near me".
 *
 * So the scan asks the WORLD instead: the tier registry deals every settlement
 * seed on the planet analytically (no materialization), which gives an exact
 * position for every candidate site at zero cost. Cells holding a seed within
 * range are then made resident through the planet's normal async cache/worker
 * batch — the same path the LOD uses — and summarized ONCE into this index.
 * Settlements are sparse (a handful of seed cells within any sane radius), so
 * this is a couple of extra region builds, off-thread, not a planet-wide grind.
 *
 * GHOSTS
 * ------
 * A dealt seed is NOT a town. Name, history and population are analytic — derived
 * from the seed — but the GROWER may build nothing on it (steep site, d_sep merge,
 * exhausted parcel budget). About a quarter of dealt sites end up with zero
 * parcels: a name and a population sitting on empty ground. Only the materialized
 * graph knows, which is the other reason this index summarizes real graphs instead
 * of trusting the seed deal: `parcels > 0` is the filter that keeps the scan
 * pointing at places that are actually THERE.
 */
import * as THREE from "three";
import {
  collapseRegion,
  localToDir,
  regionSettlements,
  settlementSeeds,
  tierRegistryFor,
  unpackCell,
  type CellId,
} from "@hyperlight/shared";
import type { Planet } from "../planet";

/** A settlement that actually exists on the ground, in PLANET-LOCAL metres. */
export interface Place {
  id: string;
  name: string;
  tier: number;
  /** Planet-local position (the planet spins — convert to world per frame). */
  local: THREE.Vector3;
}

/** One seed the tier registry dealt: a CANDIDATE site, not yet known to be built. */
export interface SeedSite {
  cell: CellId;
  key: number;
  /** Planet-local surface position of the seed. */
  local: THREE.Vector3;
}

/** Region graphs summarized per frame — one keeps the collapse/settlement pass
 *  (which walks every parcel) off the frame budget when several land at once. */
const SUMMARIES_PER_FRAME = 1;

export class PlaceIndex {
  /** Every dealt seed on a planet, computed once. Keyed by planet seed. */
  private seedCache = new Map<string, SeedSite[]>();
  /** Summarized region cells: `${planetSeed}:${packedCell}` → the places that are REAL. */
  private summarized = new Map<string, Place[]>();
  private local = new THREE.Vector3();
  private want: CellId[] = [];
  /** Region cells with a seed in range this frame, deduped (packed key → cell). */
  private inRange = new Map<number, CellId>();

  /**
   * The real settlements within `radiusM` of `fromWorld` on `planet`, and a request
   * for any region cell in range that is not resident yet (it lands within a frame
   * or two and appears then — the scan fills in as the world answers).
   */
  near(planet: Planet, fromWorld: THREE.Vector3, radiusM: number, out: Place[]): Place[] {
    out.length = 0;
    const svc = planet.regionService;
    if (svc === null) return out;

    // One frame of distance work happens in PLANET-LOCAL space: the seed sites are
    // static there, so the spin costs nothing (only the few survivors get rotated
    // back out to world by the caller).
    this.local.copy(fromWorld).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    const r2 = radiusM * radiusM;
    this.want.length = 0;
    this.inRange.clear();
    let summarized = 0;

    // Cells FIRST, deduped: a cell holds several seeds, and its places are a property
    // of the cell — walking seeds straight into the output emitted each town once per
    // seed in its cell (a triple-tagged "Iron March").
    for (const site of this.seeds(planet)) {
      if (this.local.distanceToSquared(site.local) <= r2) this.inRange.set(site.key, site.cell);
    }

    for (const [key, cell] of this.inRange) {
      const ck = `${planet.def.seed}:${key}`;
      let places = this.summarized.get(ck);
      if (places === undefined) {
        // Never grow a region on the frame thread — peek, and ask for it if absent.
        const g = svc.peek(cell);
        if (g === null) {
          this.want.push(cell);
          continue;
        }
        if (summarized >= SUMMARIES_PER_FRAME) continue; // next frame
        summarized++;
        places = this.summarize(planet, cell);
        this.summarized.set(ck, places);
      }
      for (const place of places) {
        if (this.local.distanceToSquared(place.local) <= r2) out.push(place);
      }
    }
    if (this.want.length > 0) planet.requestRegions(this.want);
    return out;
  }

  /** Planet-local → world (system space) for a place. */
  world(planet: Planet, place: Place, out: THREE.Vector3): THREE.Vector3 {
    return out.copy(place.local).applyQuaternion(planet.pose.quat).add(planet.pose.center);
  }

  /** Drop everything (a system switch replaces the planets). */
  clear(): void {
    this.seedCache.clear();
    this.summarized.clear();
  }

  /**
   * Every seed the registry dealt on this planet, as planet-local surface points —
   * every CANDIDATE site, ghosts included, computed once and cached. This is the
   * cheap analytic layer: knowing where the world MEANT to put settlements costs
   * nothing, and only the cells that fall in range are ever grown to find out which
   * of them are real.
   */
  seeds(planet: Planet): SeedSite[] {
    const cached = this.seedCache.get(planet.def.seed);
    if (cached !== undefined) return cached;
    const sites: SeedSite[] = [];
    for (const key of tierRegistryFor(planet.sampler).byCell.keys()) {
      const cell = unpackCell(key);
      for (const seed of settlementSeeds(planet.sampler, cell)) {
        const surf = planet.sampler.surfaceRadius(seed.dx, seed.dy, seed.dz);
        sites.push({
          cell,
          key,
          local: new THREE.Vector3(seed.dx, seed.dy, seed.dz).multiplyScalar(surf),
        });
      }
    }
    this.seedCache.set(planet.def.seed, sites);
    return sites;
  }

  /** The settlements a resident region ACTUALLY grew (ghost seeds dropped). */
  private summarize(planet: Planet, cell: CellId): Place[] {
    const g = planet.regionService?.peek(cell) ?? null;
    if (g === null) return [];
    const chronicle = collapseRegion(planet.sampler, g);
    const dir: number[] = [0, 0, 0];
    const out: Place[] = [];
    for (const s of regionSettlements(planet.sampler, g, chronicle)) {
      if (s.parcels === 0) continue; // a ghost: a name over empty ground
      localToDir(g.frame, s.u, s.v, dir);
      const surf = planet.sampler.surfaceRadius(dir[0], dir[1], dir[2]);
      out.push({
        id: s.id,
        name: s.name,
        tier: s.tier,
        local: new THREE.Vector3(dir[0], dir[1], dir[2]).multiplyScalar(surf),
      });
    }
    return out;
  }
}
